/** A single exportable column: `key` reads the row value, `label` becomes the CSV header cell. */
export interface CsvColumn<T> {
  key: keyof T;
  label: string;
}

/** Options for `jsonToCsv`. */
export interface CsvOptions {
  /**
   * Prefix a text cell that starts with `=`, `+`, `-`, `@`, a tab or a carriage return with `'`, so
   * a spreadsheet opens it as text instead of running it as a formula. Numbers are left alone.
   */
  neutraliseFormulas?: boolean;
}

/** UTF-8 byte-order mark: put first in a CSV file so Excel reads non-ASCII text as UTF-8. */
export const CSV_UTF8_BOM = '\uFEFF';

/** A text cell a spreadsheet would read as a formula. */
const FORMULA_START = /^[=+\-@\t\r]/;

/** Wraps a CSV cell in quotes and escapes embedded quotes if it contains a comma, quote, or line break. */
function escapeCsvCell(value: unknown, options: CsvOptions = {}): string {
  let str = '';
  if (typeof value === 'string') {
    str = options.neutraliseFormulas && FORMULA_START.test(value) ? `'${value}` : value;
  } else if (typeof value === 'number' || typeof value === 'boolean') str = value.toString();
  else if (value !== null && value !== undefined) str = JSON.stringify(value);
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/**
 * Converts an array of flat objects into a CSV string. When `columns` is
 * omitted, headers are derived from the keys of the first row.
 */
export function jsonToCsv<T extends Record<string, unknown>>(
  rows: T[],
  columns?: CsvColumn<T>[],
  options: CsvOptions = {},
): string {
  const [firstRow] = rows;
  if (firstRow === undefined) return '';
  const cols = columns ?? Object.keys(firstRow).map((key) => ({ key: key as keyof T, label: key }));
  const lines = [
    cols.map((col) => escapeCsvCell(col.label)).join(','),
    ...rows.map((row) => cols.map((col) => escapeCsvCell(row[col.key], options)).join(',')),
  ];
  return lines.join('\n');
}

/**
 * Converts `rows` to CSV via `jsonToCsv` and triggers a browser download of
 * `filename` (".csv" appended if missing). No-ops when `rows` is empty.
 */
export function downloadCsv<T extends Record<string, unknown>>(
  filename: string,
  rows: T[],
  columns?: CsvColumn<T>[],
): void {
  if (rows.length === 0) return;
  const csv = jsonToCsv(rows, columns);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
